import { USStates } from '@rategravity/core-lib/enums';
import { FactInfo } from './types';

/**
 * Facts that are on the lead payload.
 * The values for all these are constant and do not need to be evaluated.
 */
export const leadFacts: FactInfo[] = [
  {
    factId: 'lead.creditRating',
    type: 'enumerated',
    values: ['Excellent', 'Good', 'Fair', 'Poor', 'Very Poor'],
    description: `The self-reported credit tier for the lead.
    
Credit tier is consistently defined for all lead sources, except Own Up which also supports a "Very Poor" tier.
Our partners do not provide credit tier granularity below a certain point so we cannot differentiate a "Poor" vs a "Very Poor" credit lead so we assume they are "Poor".

| Credit Score | Source | Tier      |
| ------------ | ------ | --------- |
| >=720        | Any    | Excellent |
| >=680        | Any    | Good      |
| >=620        | Any    | Fair      |
| >=580        | Own Up | Poor      |
| <580         | Own Up | Very Poor |
| <620         | Others | Poor      |
`
  },
  {
    factId: 'lead.vaEligible',
    type: 'boolean',
    description: `Whether the lead is eligible for a VA loan. May be because they are a veteran or a surviving spouse.`
  },
  {
    factId: 'lead.fthb',
    type: 'boolean',
    description: `Whether the lead is a first-time home buyer.`
  },
  {
    factId: 'lead.propertyCity',
    type: 'string',
    description: `The city where the property is located.`
  },
  {
    factId: 'lead.propertyState',
    type: 'enumerated',
    values: USStates,
    description: `The state where the property is located.`
  },
  {
    factId: 'lead.propertyCounty',
    type: 'string',
    unit: 'county',
    description: `The county where the property is located.`
  },
  {
    factId: 'lead.propertyZip',
    type: 'string',
    unit: 'zip',
    description: `The zip code where the property is located.`
  },
  {
    factId: 'lead.propertyValue',
    type: 'number',
    unit: 'money',
    description: `The estimated value of the property.`
  },
  {
    factId: 'lead.propertyType',
    type: 'enumerated',
    values: [
      'SingleFamily',
      'MultiFamily',
      'Condo',
      'Manufactured',
      'Townhome',
      'NewConstruction',
      'Mobile'
    ],
    description: `The type of property.`
  },
  {
    factId: 'lead.propertyUse',
    type: 'enumerated',
    values: ['Primary', 'Secondary', 'Investment'],
    description: `The intended use of the property.`
  },
  {
    factId: 'lead.loanPurpose',
    type: 'enumerated',
    values: ['Purchase', 'Refinance'],
    description: `The purpose of the loan.`
  },
  {
    factId: 'lead.loanAmount',
    type: 'number',
    unit: 'money',
    description: `The amount of the loan.`
  },
  {
    factId: 'lead.cashOutAmount',
    type: 'number',
    unit: 'money',
    description: `For a refinance scenario, how much money the shopper is looking to take out.`
  },
  {
    factId: 'lead.currentMortgageBalance',
    type: 'number',
    unit: 'money',
    description: `The current balance on the shopper's mortgage.`
  },
  {
    factId: 'lead.currentMortgageRate',
    type: 'number',
    unit: 'percentWhole',
    description: `When refinancing, the shopper's current mortgage rate.
For purchase deals, this is the most competitive rate they have been quoted.
(Stored as \`4.25\` for \`4.25%\`)`
  },
  {
    factId: 'lead.jornayaLeadId',
    type: 'string',
    description: `The Jornaya lead ID associated with this lead.`
  },
  {
    factId: 'lead.trustedFormCertificateUrl',
    type: 'string',
    description: `The TrustedForm certificate URL associated with this lead.`
  },
  {
    factId: 'lead.source',
    type: 'enumerated',
    values: ['Own Up', 'rdc', 'mrc', 'bankrate', 'redfin', 'movoto', 'experian'],
    description: `Where this lead was sourced from.`
  },
  {
    factId: 'lead.sourceChannel',
    type: 'string',
    description: `The channel through which the lead was sourced.`
  },
  {
    factId: 'lead.advisor',
    type: 'string',
    description: `The email of the advisor "featured" on this lead. If this was a referral questionnaire, this is the email of the advisor who referred the lead.`
  },
  {
    factId: 'lead.leadPricePaid',
    type: 'number',
    unit: 'money',
    description: `The price paid for this lead (if provided.)`
  },
  {
    factId: 'lead.leadType',
    type: 'enumerated',
    values: [
      'web-lead',
      'direct-web-lead',
      'preapproval-lead',
      'rate-quote-lead',
      'click-out-lead',
      'rate-table-lead',
      'recapture-from-concierge-lead'
    ],
    description: `The type of lead. Dictates what ruleset is run.
 - \`web-lead\`: Leads received from one of our web forms, including the questionnaire and partner channels.
 - \`direct-web-lead\`: Leads manually being sold from Advisor Hub.
 - \`preapproval-lead\`: Preapproval Leads manually being sold from Advisor Hub.
 - \`rate-quote-lead\`: Leads quoted rates during a consult and manually being sold from Advisor Hub.
 - \`click-out-lead\`: Leads that connected with lenders via our lender table widget. These leads get directed to the lender's site.
 - \`rate-table-lead\`: Leads that connected with lenders via our rate table widget. These leads get directed to the lender's site.
 - \`recapture-from-concierge-lead\`: Leads that are recaptured from the concierge service that have not responded.`
  },
  {
    factId: 'lead.questionnaireType',
    type: 'enumerated',
    values: ['purchase', 'preapproval', 'refinance', 'accepted-offer'],
    description: `If the lead came from the Own Up questionnaire, what path they came from.`
  },
  {
    factId: 'lead.bankruptcy',
    type: 'boolean',
    description: `Whether the lead has recently declared bankruptcy.`
  },
  {
    factId: 'lead.dateLeadReceived',
    type: 'number',
    unit: 'timestamp',
    isDate: true,
    description: `The timestamp when the lead was received.`
  },
  /** Employment status */
  {
    factId: 'lead.employmentStatus',
    type: 'enumerated',
    values: ['Employed', 'SelfEmployed', 'Retired', 'Unemployed'],
    description: `DEPRECATED - please use incomeType instead.
The employment status of the lead.`
  },
  {
    factId: 'lead.hasCoborrower',
    type: 'boolean',
    description: `Whether the lead has a co-borrower.`
  },
  {
    factId: 'lead.hasAgent',
    type: 'boolean',
    description: `Whether the lead is working with a real estate agent already.`
  },
  {
    factId: 'lead.hasPreapproval',
    type: 'boolean',
    description: `Whether the lead has a preapproval.`
  },
  {
    factId: 'lead.questionnaireStartTime',
    type: 'number',
    unit: 'timestamp',
    isDate: true,
    description: `The timestamp when the Own Up questionnaire was started.`
  },
  {
    factId: 'lead.questionnaireCompletionTime',
    type: 'number',
    unit: 'timestamp',
    isDate: true,
    description: `The timestamp when the Own Up questionnaire was completed.`
  },
  {
    factId: 'lead.purchaseTimeline',
    type: 'number',
    unit: 'months',
    description: `The number of months until the lead is looking to purchase a home.`
  },
  {
    factId: 'lead.closeDate',
    type: 'number',
    unit: 'timestamp',
    isDate: true,
    description: `The date at which the lead is looking to close on their home.`
  },
  {
    factId: 'lead.grossIncome',
    type: 'number',
    unit: 'money',
    description: `The gross income of the lead.`
  },
  {
    factId: 'lead.loanType',
    type: 'enumerated',
    values: ['Conventional', 'FHA', 'HELOC', 'Reverse', 'USDA', 'VA'],
    description: `The type of loan the lead is looking for.`
  },
  {
    factId: 'lead.campaign',
    type: 'string',
    description: `The partner channel campaign associated with this lead.`
  },
  {
    factId: 'lead.campaignId',
    type: 'string',
    description: `The partner channel campaign ID associated with this lead.`
  },
  {
    factId: 'lead.hasAcceptedOffer',
    type: 'boolean',
    description: `Whether the lead has accepted an offer on a home already.`
  },
  {
    factId: 'lead.downPaymentPct',
    type: 'number',
    unit: 'percent',
    description: `The down payment as a percentage of the property value in decimal format. (Stored as \`0.15\` for \`15%\`)`
  },
  {
    factId: 'lead.ltv',
    type: 'number',
    unit: 'percent',
    description: `The loan-to-value ratio in decimal format. (Stored as \`0.85\` for \`85%\`)`
  },
  {
    factId: 'lead.subsource',
    type: 'string',
    description: `A secondary source for this lead, typically derived from the context of a prior pageview.`
  }
];

/**
 * leadEvaluatedFacts are not on the lead, but depend on lead properties.
 *  They require some asyncronous evaluation, which means they are not runtime facts
 *  and is why the are not prepended with "lead".
 */
export const leadEvaluatedFacts: FactInfo[] = [
  {
    factId: 'utmSource',
    type: 'string',
    description: `The UTM source associated with this lead's most recent visit to the \`ownup.com\` domain within the last 24 hours.`
  },
  {
    factId: 'utmTerm',
    type: 'string',
    description: `The UTM term associated with this lead's most recent visit to the \`ownup.com\` domain within the last 24 hours.`
  },
  {
    factId: 'utmMedium',
    type: 'string',
    description: `The UTM medium associated with this lead's most recent visit to the \`ownup.com\` domain within the last 24 hours.`
  },
  {
    factId: 'utmContent',
    type: 'string',
    description: `The UTM content associated with this lead's most recent visit to the \`ownup.com\` domain within the last 24 hours.`
  },
  {
    factId: 'utmCampaign',
    type: 'string',
    description: `The UTM campaign associated with this lead's most recent visit to the \`ownup.com\` domain within the last 24 hours.`
  },
  {
    factId: 'isRocketDuplicate',
    type: 'boolean',
    description: `Whether the lead is a duplicate from Rocket Mortgage.`
  },
  {
    factId: 'oneUnitConformingLoanLimit',
    type: 'number',
    unit: 'money',
    description: `The conforming loan limit for a one-unit property in the lead's county.`
  },
  {
    factId: 'twoUnitConformingLoanLimit',
    type: 'number',
    unit: 'money',
    description: `The conforming loan limit for a two-unit property in the lead's county.`
  },
  {
    factId: 'threeUnitConformingLoanLimit',
    type: 'number',
    unit: 'money',
    description: `The conforming loan limit for a three-unit property in the lead's county.`
  },
  {
    factId: 'fourUnitConformingLoanLimit',
    type: 'number',
    unit: 'money',
    description: `The conforming loan limit for a four-unit property in the lead's county.`
  },
  {
    factId: 'leadHourOfDay',
    type: 'enumerated',
    values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
    description: `The current hour of the day in the state of the lead.`
  },
  {
    factId: 'leadTimezone',
    type: 'enumerated',
    values: [
      'America/Anchorage',
      'America/Arizona',
      'America/Chicago',
      'America/Denver',
      'Pacific/Honolulu',
      'America/Los_Angeles',
      'America/New_York'
    ],
    description: `The timezone of the state of the lead`
  },
  {
    factId: 'isWithinTcpaDialingHours',
    type: 'boolean',
    description: `Whether the current local time of the lead's state is within the TCPA dialing hours for the lead's state.`
  }
];

/**
 * General facts that could be used anywhere.
 */
export const generalFacts: FactInfo[] = [
  {
    factId: 'hourOfDay',
    type: 'enumerated',
    values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
    description: `The hour of the day the lead was received. (Eastern time, military format)`
  },
  {
    factId: 'dayOfTheWeek',
    type: 'enumerated',
    values: [0, 1, 2, 3, 4, 5, 6],
    description: `The current day of the week. (0 = Sunday, 1 = Monday, ...)`
  },
  {
    factId: 'currentDay',
    type: 'enumerated',
    values: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    description: `The current day of the week.`
  },
  {
    factId: 'currentMonth',
    type: 'enumerated',
    values: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ],
    description: `The current month.`
  },
  { factId: 'currentYear', type: 'number', unit: 'year', description: `The current year.` },
  {
    factId: 'currentTimestamp',
    type: 'number',
    unit: 'timestamp',
    isDate: true,
    description: `The current timestamp.`
  },
  {
    factId: 'randomNumber',
    type: 'number',
    description: `A random number between 0 and 1. Note that this will be different for every instance of \`randomNumber\` used.`
  },
  {
    factId: 'pricingTier',
    type: 'number',
    unit: 'money',
    params: {
      tier: {
        optional: false,
        type: 'enumerated',
        values: [
          'T1 AO',
          'T1 AO Excellent/Good',
          'T1 AO Fair',
          'T1 AO Niche',
          'T1 Cashout Refi',
          'T1 Click',
          'T1 Niche',
          'T1 Non-AO Purchase',
          'T1 Overflow',
          'T1 PAL',
          'T1 Rate Only Refi',
          'T1 VA Purchase',
          'T2 AO',
          'T2 AO Excellent/Good',
          'T2 AO Fair',
          'T2 AO Niche',
          'T2 Cashout Refi',
          'T2 Click',
          'T2 Niche',
          'T2 Non-AO Purchase',
          'T2 Overflow',
          'T2 PAL',
          'T2 Rate Only Refi',
          'T2 VA Purchase',
          'T3 AO',
          'T3 AO Excellent/Good',
          'T3 AO Fair',
          'T3 AO Niche',
          'T3 Cashout Refi',
          'T3 Click',
          'T3 Niche',
          'T3 Non-AO Purchase',
          'T3 Overflow',
          'T3 PAL',
          'T3 Rate Only Refi',
          'T3 VA Purchase'
        ],
        description: `The pricing tier to retrieve.`
      }
    },
    description: `Global constants for pricing tiers.`
  },
  {
    factId: 'splitConfig',
    type: 'object',
    params: {
      splitName: {
        optional: false,
        type: 'string',
        description: `The name of the Split to check.`
      },
      splitKey: {
        optional: true,
        type: 'string',
        description: `The key that should be associated with this split. Defaults to \`leadID\`.`
      },
      defaultConfig: {
        optional: true,
        type: 'object',
        properties: {},
        description: `What to return if nothing is found.`
      }
    },
    properties: {},
    description: `Retrieve a Split configuration value.`
  },
  {
    factId: 'splitTreatment',
    type: 'string',
    params: {
      splitName: {
        optional: false,
        type: 'string',
        description: `The name of the Split to check.`
      },
      splitKey: {
        optional: true,
        type: 'string',
        description: `The key that should be associated with this split. Defaults to \`leadID\`.`
      }
    },
    description: `Retrieve a Split treatment value.`
  }
];

/**
 * The lender campaign rules engine outputs a match object, these
 *  are facts reference that object.
 */
export const matchFacts: FactInfo[] = [
  {
    factId: 'match.lenderId',
    type: 'string',
    description: 'ID of the matched lender'
  },
  { factId: 'match.campaign', type: 'string', description: 'The campaign of the matched lender' },
  {
    factId: 'match.leadCost',
    type: 'number',
    description: 'The base cost of the lead'
  },
  {
    factId: 'match.budgetType',
    type: 'string',
    description: 'The type of budget to use'
  },
  {
    factId: 'match.method',
    type: 'enumerated',
    values: ['auto', 'opt-in'],
    description: 'The method of selling the lead'
  },
  {
    factId: 'match.processDelayedLeadTimestamp',
    type: 'number',
    description: 'If the lead is to be delayed, the timestmap to sell the lead'
  },
  {
    factId: 'match.isClearinghouse',
    type: 'boolean',
    description: 'If the lead is a clearinghouse lead'
  },
  // Budget categorization isn't on the match, but it depends on the match
  //  (for the lenderId and budgetType), so including it as a matchFact
  {
    factId: 'budgetCategorization',
    type: 'list',
    typeInfo: {
      type: 'string'
    },
    description: 'The budget categorizations for the lender'
  }
];

/**
 * All facts, this should include all possible facts.
 * This is used in some helpers to help format the fact correctly.
 */
export const allFacts = [...leadFacts, ...leadEvaluatedFacts, ...generalFacts, ...matchFacts];

interface FactCategory {
  title: string;
  description: string;
  facts: FactInfo[];
  engines: string[];
}
const lenderCampaignEngine = 'Lender Campaign Rules';
const pricingEngine = 'Pricing Rules';
const lenderEndpointEngine = 'Lender Endpoint Rules';
/**
 * The breakdown of the different fact categories.
 * This is used for documentation purposes to update documentation in notion.
 *  (See the sync-to-notion script)
 * Make sure to update this if any new engines are added.
 *  Also add the engine to the `ruleEvents` map in the notion script.
 *  (Can't do it here because it causes a circular dependency)
 */
export const factCategories: Record<string, FactCategory> = {
  leadFacts: {
    title: 'Lead Facts',
    description:
      'Facts that are on the lead payload.  These are constant and do not need to be evaluated',
    facts: leadFacts,
    engines: [lenderCampaignEngine, pricingEngine, lenderEndpointEngine]
  },
  leadEvaluatedFacts: {
    title: 'Lead Evaluation Facts',
    description: 'Facts that depend on the lead, but require additional evaluation',
    facts: leadEvaluatedFacts,
    engines: [lenderCampaignEngine]
  },
  generalFacts: {
    title: 'General Facts',
    description: 'General helper facts',
    facts: generalFacts,
    engines: [lenderCampaignEngine, pricingEngine]
  },
  matchFacts: {
    title: 'Match Facts',
    description: 'Facts that reference the lender campaign match output',
    facts: matchFacts,
    engines: [pricingEngine, `${lenderCampaignEngine} (partial)`]
  }
};
